<template>
  <div class="visitor-day">
    <div class="zone-total">
      <div class="widget__label widget__label-thin">
        Total OTS
        <span class="zone-total__count">{{ displayData }}</span>
      </div>
    </div>

    <div v-if="chartSeries.length" class="chart-wrapper">
      <apexchart type="bar" :options="chartOptions" :series="chartSeries" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat.js';
dayjs.extend(advancedFormat);

import { barColors } from '@/utils/chartColors';

import type { WidgetFilter, WidgetSetting, WidgetTypeVisits } from '@/types/Widget';

import type { BaseChartOptions } from '@/utils/chartSettings';
import { getChartExportSettings, getDefaultChartOptions } from '@/utils/chartSettings';
import { formatNumber } from '@/utils/formatNumbers';

const props = defineProps<{
  widget: WidgetTypeVisits;
  time?: WidgetFilter;
  day?: WidgetSetting;
}>();

const chartSettings = computed(() => {
  if (props.widget.data && props.widget.data['Site']) {
    const dailyData = props.widget.data['Site']['Total'];

    if (dailyData) {
      const dataMap = Object.keys(dailyData).map((day) => {
        const date = dayjs(day, 'YYYYMMDD').format('YYYY-MM-DD');

        if (dailyData) {
          return {
            name: date,
            data: [dailyData[day]],
          };
        }
      });
      return dataMap.filter((map) => map?.name != 'Invalid Date');
    }
  }

  return [];
});

const chartSeries = computed(() => {
  if (chartSettings.value) {
    const dataPoints = chartSettings.value.map((point) => {
      return point?.data;
    });

    return [
      {
        name: 'OTS',
        data: dataPoints.flat(),
      },
    ];
  }

  return [];
});

const chartLabels = computed(() => {
  if (chartSettings.value) {
    return chartSettings.value.map((point) => point?.name);
  }
  return [];
});

const defaultChartOptions = getDefaultChartOptions(props.widget.code_name);

const chartOptions = ref<BaseChartOptions>({
  ...defaultChartOptions.value,
  chart: {
    ...defaultChartOptions.value.chart,
    id: 'meshh-chart-visitor-day',
    type: 'bar',
    stacked: true,
    toolbar: getChartExportSettings(props.widget.name, 'Site', props.time?.name || '', '', '', ''),
  },
  colors: barColors,
  xaxis: {
    categories: chartLabels.value,
    show: true,
    labels: {
      formatter(value: string) {
        return dayjs(value).format('Do MMM');
      },
    },
  },
  grid: {
    show: false,
  },
  dataLabels: {
    enabled: false,
  },
  tooltip: {
    y: {
      formatter: (val: number) => {
        return formatNumber(val, 0);
      },
    },
  },
});

const displayData = computed(() => {
  const data = props.widget.data;

  if (data && data['Site']) {
    return props.widget.data['Site']['Total'];
  }

  return '';
});
</script>

<style lang="scss">
.widget .visitor-day .widget__label.widget__label-thin {
  display: flex;
}
</style>
